import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';

import { temporaryFieldSaver } from 'store/actions/temporaryFieldSaverActions';
import { getFullName, utilHelper } from 'modules/common/helpers';

import { Button, Modal } from 'modules/common/components';
import { StyledOr } from 'modules/elements/components';
import { HeirContactForm } from 'modules/heir/components';

import iconPlus from 'images/icon-plus.svg';
import { ContactCard } from 'modules/myContacts/components';

export interface HeirContactsProps {
  previousAnswer?: ContactDTO[];
}

const HeirContacts = ({ previousAnswer }: HeirContactsProps) => {
  const { t } = useTranslation(['common', 'assistant']);
  const dispatch = useDispatch();

  const [showForm, setShowForm] = useState(false);
  const [data, setData] = useState<ContactDTO[]>(previousAnswer || []);
  const [dataToEdit, setDataToEdit] = useState<ContactDTO>({} as ContactDTO);
  const [addedFromContacts, setAddedFromContacts] = useState(false);

  const contactsData: ContactDTO[] =
    useSelector((state: any) => state.contacts.list) || [];

  // filter all the contacts that have been used for creating heirs
  const filteredContacts = contactsData.filter(
    (contact) => !data.find((heir) => heir.id === contact.id)
  );

  // show previous input val, if entered before
  useEffect(() => {
    if (previousAnswer) {
      dispatch(temporaryFieldSaver(previousAnswer));
    }
  }, [previousAnswer]);

  useEffect(() => {
    dispatch(temporaryFieldSaver(data));
  }, [data]);

  const handleHeirFormSubmit = async (
    isNewHeir: boolean,
    submitData: ContactDTO
  ) => {
    // if the user creates the heir from an empty form or heir data taken from contacts
    if (isNewHeir || addedFromContacts) {
      setData([...data, submitData]);
    } else {
      setData((prev) => {
        const copiedState = [...prev];

        const idx = copiedState.findIndex((item) => item.id === submitData.id);
        copiedState[idx] = submitData;
        return copiedState;
      });
    }
    setAddedFromContacts(false);
    handleFormModalClose();
  };

  const onEditHeir = (heir) => {
    setDataToEdit(heir);
    setShowForm(true);
  };

  const handleFormModalClose = () => {
    setShowForm(false);
    setDataToEdit({} as ContactDTO);
    setAddedFromContacts(false);
  };

  const onDeleteHeir = (heir) => {
    const copiedData = [...data];
    const idx = copiedData.findIndex((item) => item.id === heir.id);
    copiedData.splice(idx, 1);
    setData(copiedData);
  };

  const handleSelectChange = (selectedOption): void => {
    const option = selectedOption as SelectOption;

    const selectedContact = (filteredContacts.find(
      (contact) => contact.id === option.value
    ) || {}) as ContactDTO;
    // set the selected contact to add as a heir form
    setAddedFromContacts(true);
    setDataToEdit(selectedContact);
    setShowForm(true);
  };

  const contactsOptions = utilHelper.getSelectOptions(filteredContacts);
  const selectedOption = addedFromContacts
    ? contactsOptions.find((option) => option.value === dataToEdit.id)
    : undefined;

  return (
    <>
      {showForm && (
        <Modal show onHide={handleFormModalClose}>
          <HeirContactForm
            onCancel={handleFormModalClose}
            onSuccess={handleHeirFormSubmit}
            contactItem={dataToEdit}
          />
        </Modal>
      )}

      <div
        className="card xs:t-w-full md:t-w-4/5 xs:t-p-4 md:t-p-7"
        data-testid="HeirContacts"
      >
        <div className="Form-group">
          <label className="Form-label" htmlFor="contact">
            {t('common:from_existing_contact')}
          </label>

          <Select
            id="contact"
            name="contact"
            value={selectedOption}
            onChange={handleSelectChange}
            options={contactsOptions}
            className="Select"
            classNamePrefix="Select"
            placeholder={t('common:default_select_placeholder')}
          />
        </div>

        <div className="d-flex flex-column align-items-center">
          <StyledOr>{t('common:or')}</StyledOr>

          <Button category="secondary" onClick={() => setShowForm(true)}>
            <img
              src={iconPlus}
              className="button-icon-amazon"
              alt={t('common:add_new_heir')}
              role="presentation"
            />
            {t('common:add_new_heir')}
          </Button>

          <div className="u-pb-15" />

          <div>
            {data?.map((item) => (
              <ContactCard
                key={getFullName(item, false)}
                contactData={item}
                onDelete={onDeleteHeir}
                onEdit={onEditHeir}
                className="card xl:t-py-6 xl:t-px-7 xs:t-py-3 xs:t-px-3.5 xl:t-max-w-sm"
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default HeirContacts;
